import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Bell,
  Send,
  CheckCircle,
  Users,
  Smartphone,
  Globe,
  RefreshCw,
  Eye,
  Trash2,
} from "lucide-react";
import API_BASE from "../../config/api";

/* ================= STYLES ================= */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => props.$bg || "rgba(245, 215, 110, 0.12)"};
    color: ${(props) => props.$color || "#f5d76e"};
  }

  .title {
    font-size: 0.75rem;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  .value {
    font-size: 1.4rem;
    font-weight: 700;
    color: #f3f4f6;
    margin-top: 2px;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 1.5rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1f293d;
  padding-bottom: 0.85rem;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #f5d76e;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  span {
    font-size: 0.78rem;
    color: #94a3b8;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .counter {
    font-size: 0.72rem;
    color: #64748b;
  }

  input,
  textarea,
  select {
    background: #0b0f19;
    border: 1px solid #2d3748;
    border-radius: 8px;
    padding: 0.65rem 0.85rem;
    color: #f8fafc;
    font-size: 0.9rem;
    font-family: inherit;
    outline: none;
    transition: border 0.15s ease;

    &:focus {
      border-color: #f5d76e;
    }
  }

  textarea {
    resize: vertical;
    min-height: 85px;
  }
`;

const ImageModeTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.4rem;

  button {
    background: ${(props) => (props.$active ? "#f5d76e" : "#1f293d")};
    color: ${(props) => (props.$active ? "#000" : "#94a3b8")};
    font-size: 0.75rem;
    font-weight: 700;
    border: none;
    border-radius: 6px;
    padding: 0.35rem 0.75rem;
    cursor: pointer;
  }
`;

const ImagePreviewBox = styled.div`
  position: relative;
  width: 100%;
  max-height: 180px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #374151;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 180px;
    object-fit: contain;
  }

  button {
    position: absolute;
    top: 6px;
    right: 6px;
    background: rgba(239, 68, 68, 0.85);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 0.5rem;

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.65rem 1.25rem;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    border: none;
    transition: all 0.15s ease;
  }

  .btn-preview {
    background: #1f293d;
    color: #cbd5e1;
    &:hover {
      background: #2d3748;
    }
  }

  .btn-test {
    background: #334155;
    color: #f8fafc;
    &:hover {
      background: #475569;
    }
  }

  .btn-send {
    background: #f5d76e;
    color: #000;
    margin-left: auto;
    box-shadow: 0 4px 15px rgba(245, 215, 110, 0.3);
    &:hover {
      background: #e6c555;
      transform: translateY(-1px);
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

/* Realistic Phone Preview Component */
const PhonePreview = styled.div`
  background: #000;
  border: 2px solid #2d3748;
  border-radius: 20px;
  padding: 14px;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);

  .phone-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.65rem;
    color: #94a3b8;
    margin-bottom: 12px;
  }

  .notification-bubble {
    background: #1e293b;
    border: 1px solid rgba(245, 215, 110, 0.3);
    border-radius: 12px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .app-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.65rem;
    color: #f5d76e;
    font-weight: 700;
    img {
      width: 14px;
      height: 14px;
      border-radius: 3px;
    }
  }

  .notif-title {
    font-size: 0.82rem;
    font-weight: 700;
    color: #fff;
    line-height: 1.2;
  }

  .notif-body {
    font-size: 0.72rem;
    color: #cbd5e1;
    line-height: 1.35;
  }

  .notif-img {
    width: 100%;
    max-height: 130px;
    object-fit: cover;
    border-radius: 8px;
    margin-top: 4px;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
    text-align: left;

    th {
      background: #111827;
      color: #94a3b8;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #1f293d;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.7rem;
      letter-spacing: 0.05em;
    }

    td {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #1f293d;
      color: #e2e8f0;
      vertical-align: middle;
    }

    tr:hover td {
      background: rgba(255, 255, 255, 0.02);
    }
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;

  background: ${(props) =>
    props.$type === "completed" || props.$type === "accepted_by_fcm"
      ? "rgba(34, 197, 94, 0.15)"
      : props.$type === "failed"
      ? "rgba(239, 68, 68, 0.15)"
      : "rgba(245, 215, 110, 0.15)"};

  color: ${(props) =>
    props.$type === "completed" || props.$type === "accepted_by_fcm"
      ? "#86efac"
      : props.$type === "failed"
      ? "#fca5a5"
      : "#f5d76e"};

  border: 1px solid
    ${(props) =>
      props.$type === "completed" || props.$type === "accepted_by_fcm"
        ? "rgba(34, 197, 94, 0.3)"
        : props.$type === "failed"
        ? "rgba(239, 68, 68, 0.3)"
        : "rgba(245, 215, 110, 0.3)"};
`;

/* ================= COMPONENT ================= */
const AdminNotificationsTab = () => {
  const token = localStorage.getItem("adminToken");

  // Stats state
  const [stats, setStats] = useState({
    totalSubscribers: 0,
    androidSubscribers: 0,
    webSubscribers: 0,
    promoConsented: 0,
  });

  // Composer state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageMode, setImageMode] = useState("url"); // "upload" | "url"
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [destination, setDestination] = useState("/#rates");
  const [customDestination, setCustomDestination] = useState("");
  const [audience, setAudience] = useState("all");
  const [testToken, setTestToken] = useState("");

  // UI status & dialogs
  const [sending, setSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchCampaigns();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/notifications/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.stats) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error("Failed to fetch notification stats:", err);
    }
  };

  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const res = await fetch(`${API_BASE}/api/notifications/admin/campaigns?limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.campaigns) {
        setCampaigns(data.campaigns);
      }
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleImageFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      alert("Image size exceeds 3MB limit");
      return;
    }

    setImageFile(file);
    setUploadingImage(true);

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result;
        // Upload to persistent backend media store
        const res = await fetch(`${API_BASE}/api/notifications/admin/upload-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            imageBase64: base64,
            filename: file.name,
            mimeType: file.type,
          }),
        });

        const data = await res.json();
        if (data.success && data.imageUrl) {
          setImageUrl(data.imageUrl);
          setStatusMsg("✅ Image uploaded to persistent storage");
        } else {
          alert(data.message || "Upload failed");
        }
      } catch (err) {
        alert("Image upload error: " + err.message);
      } finally {
        setUploadingImage(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const finalDestination = destination === "custom" ? customDestination || "/#rates" : destination;

  const handleSendCampaign = async () => {
    if (!title.trim() || !body.trim()) {
      alert("Title and message body are required");
      return;
    }

    setSending(true);
    setStatusMsg("");

    try {
      const idempotencyKey = `blast_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      const res = await fetch(`${API_BASE}/api/notifications/admin/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          imageUrl: imageUrl.trim(),
          destination: finalDestination,
          audience,
          idempotencyKey,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setStatusMsg(`✅ Campaign successfully sent! (Attempted: ${data.campaign?.stats?.attempted || 0}, Success: ${data.campaign?.stats?.successful || 0})`);
        setTitle("");
        setBody("");
        setImageUrl("");
        setImageFile(null);
        setShowConfirmModal(false);
        fetchStats();
        fetchCampaigns();
      } else {
        setStatusMsg(`❌ Failed: ${data.message}`);
      }
    } catch (err) {
      setStatusMsg(`❌ Error sending campaign: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    let target = testToken.trim();
    if (!target) {
      // Try using current browser token if available
      target = localStorage.getItem("gj_fcm_token");
      if (!target) {
        const input = prompt("Enter the device FCM token for test send:");
        if (!input) return;
        target = input.trim();
        setTestToken(target);
      }
    }

    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/notifications/admin/send-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          testToken: target,
          title: title || "🧪 Test Notification from Admin",
          body: body || "Test push notification content.",
          imageUrl: imageUrl.trim(),
          destination: finalDestination,
          idempotencyKey: `test_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert("✅ Test notification sent successfully!");
      } else {
        alert("❌ Test send failed: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      alert("❌ Test error: " + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Container>
      {/* 📊 Top Stats */}
      <StatsGrid>
        <StatCard>
          <div className="icon-wrap">
            <Users size={22} />
          </div>
          <div>
            <div className="title">Total Subscribers</div>
            <div className="value">{stats.totalSubscribers}</div>
          </div>
        </StatCard>

        <StatCard $bg="rgba(59, 130, 246, 0.12)" $color="#60a5fa">
          <div className="icon-wrap">
            <Smartphone size={22} />
          </div>
          <div>
            <div className="title">Android Devices</div>
            <div className="value">{stats.androidSubscribers}</div>
          </div>
        </StatCard>

        <StatCard $bg="rgba(168, 85, 247, 0.12)" $color="#c084fc">
          <div className="icon-wrap">
            <Globe size={22} />
          </div>
          <div>
            <div className="title">Web Browsers</div>
            <div className="value">{stats.webSubscribers}</div>
          </div>
        </StatCard>

        <StatCard $bg="rgba(34, 197, 94, 0.12)" $color="#4ade80">
          <div className="icon-wrap">
            <CheckCircle size={22} />
          </div>
          <div>
            <div className="title">Promotional Opt-Ins</div>
            <div className="value">{stats.promoConsented}</div>
          </div>
        </StatCard>
      </StatsGrid>

      {/* 📝 Main Composer & Phone Preview */}
      <MainGrid>
        <Card>
          <CardHeader>
            <h3>
              <Bell size={18} />
              Notification Campaign Composer
            </h3>
            <span>Rich Image & Text Broadcast</span>
          </CardHeader>

          {/* Title */}
          <FormGroup>
            <label>
              Notification Title *
              <span className="counter">{title.length} / 120</span>
            </label>
            <input
              type="text"
              placeholder="e.g. 🪙 Special Silver Rate Offer from GJ Bullion!"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormGroup>

          {/* Message Body */}
          <FormGroup>
            <label>
              Notification Message (Body) *
              <span className="counter">{body.length} / 500</span>
            </label>
            <textarea
              placeholder="e.g. Check today's live Silver Chorsa rates on GJ Bullion. Explore our exclusive bullion rates and instant booking."
              value={body}
              maxLength={500}
              onChange={(e) => setBody(e.target.value)}
            />
          </FormGroup>

          {/* Image Upload / URL Attachment */}
          <FormGroup>
            <label>Notification Image (Optional)</label>
            <ImageModeTabs $active={imageMode === "url"}>
              <button type="button" onClick={() => setImageMode("url")}>
                Paste Image URL
              </button>
              <button type="button" onClick={() => setImageMode("upload")}>
                Upload File (Max 3MB)
              </button>
            </ImageModeTabs>

            {imageMode === "url" ? (
              <input
                type="text"
                placeholder="https://example.com/festival-offer.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
            ) : (
              <div>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/webp"
                  onChange={handleImageFileSelect}
                  disabled={uploadingImage}
                />
                {uploadingImage && <span style={{ fontSize: "0.75rem", color: "#f5d76e" }}>Uploading to persistent MongoDB storage...</span>}
              </div>
            )}

            {imageUrl && (
              <ImagePreviewBox>
                <img src={imageUrl} alt="Preview" onError={() => alert("Image URL could not be loaded")} />
                <button type="button" onClick={() => { setImageUrl(""); setImageFile(null); }}>
                  <Trash2 size={12} /> Remove
                </button>
              </ImagePreviewBox>
            )}
          </FormGroup>

          {/* Destination */}
          <FormGroup>
            <label>Click Destination (Where notification opens)</label>
            <select value={destination} onChange={(e) => setDestination(e.target.value)}>
              <option value="/#rates">Live Bullion Rates (/#rates)</option>
              <option value="/trade/gold_999/buy">Instant Gold 999 Buy (/trade/gold_999/buy)</option>
              <option value="/trade/silver_chorsa/buy">Instant Silver Chorsa Buy (/trade/silver_chorsa/buy)</option>
              <option value="/trades">Customer Trade History (/trades)</option>
              <option value="custom">Custom Internal Route</option>
            </select>

            {destination === "custom" && (
              <input
                type="text"
                placeholder="/about or /privacy-policy"
                value={customDestination}
                onChange={(e) => setCustomDestination(e.target.value)}
              />
            )}
          </FormGroup>

          {/* Target Audience */}
          <FormGroup>
            <label>Target Audience</label>
            <select value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="all">All Subscribers (Consented to promotions)</option>
              <option value="android">Android App Users Only</option>
              <option value="web">Website Browsers Only</option>
            </select>
          </FormGroup>

          {statusMsg && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                background: statusMsg.startsWith("✅") ? "rgba(34, 197, 94, 0.15)" : "rgba(239, 68, 68, 0.15)",
                color: statusMsg.startsWith("✅") ? "#86efac" : "#fca5a5",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              {statusMsg}
            </div>
          )}

          {/* Action Row */}
          <ActionRow>
            <button type="button" className="btn-test" onClick={handleSendTest} disabled={sending}>
              🧪 Test Send
            </button>

            <button
              type="button"
              className="btn-send"
              disabled={sending || !title.trim() || !body.trim()}
              onClick={() => setShowConfirmModal(true)}
            >
              <Send size={15} />
              {sending ? "Sending..." : "Send Campaign Blast"}
            </button>
          </ActionRow>
        </Card>

        {/* 📱 Real-time Mobile Preview */}
        <Card>
          <CardHeader>
            <h3>
              <Eye size={18} /> Live Device Preview
            </h3>
            <span>How recipients see it</span>
          </CardHeader>

          <PhonePreview>
            <div className="phone-header">
              <span>12:30 PM</span>
              <span>📶 5G  🔋 98%</span>
            </div>

            <div className="notification-bubble">
              <div className="app-tag">
                <img src="/assets/logo.jpeg" alt="GJ" />
                <span>GJ BULLION • now</span>
              </div>

              <div className="notif-title">
                {title.trim() || "Notification Title Preview"}
              </div>

              <div className="notif-body">
                {body.trim() || "Your message body will appear here. Live rate updates, festive offers, and market alerts."}
              </div>

              {imageUrl && <img src={imageUrl} alt="attachment" className="notif-img" />}
            </div>
          </PhonePreview>

          <div
            style={{
              padding: "12px",
              background: "rgba(245, 215, 110, 0.06)",
              border: "1px solid rgba(245, 215, 110, 0.2)",
              borderRadius: "10px",
              fontSize: "0.78rem",
              color: "#cbd5e1",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <div style={{ fontWeight: 700, color: "#f5d76e" }}>Compliance & Safeguards:</div>
            <div>• Recipients who opted out of promotional messages are automatically skipped.</div>
            <div>• Enforces 3 automated messages daily limit per user.</div>
            <div>• Click destination will open: <code>{finalDestination}</code></div>
          </div>
        </Card>
      </MainGrid>

      {/* 📜 Campaign History */}
      <Card>
        <CardHeader>
          <h3>
            <RefreshCw size={18} />
            Sent Campaigns & Audit Log
          </h3>
          <button
            onClick={fetchCampaigns}
            disabled={loadingCampaigns}
            style={{
              background: "none",
              border: "none",
              color: "#f5d76e",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "0.8rem",
            }}
          >
            <RefreshCw size={13} className={loadingCampaigns ? "animate-spin" : ""} /> Refresh
          </button>
        </CardHeader>

        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>Campaign ID</th>
                <th>Title</th>
                <th>Type</th>
                <th>Target</th>
                <th>Sent At (IST)</th>
                <th title="Total messages accepted by Google FCM servers for routing">FCM Accepted</th>
                <th>Failed</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", color: "#64748b", padding: "2rem" }}>
                    No notification campaigns sent yet.
                  </td>
                </tr>
              ) : (
                campaigns.map((c) => (
                  <tr key={c._id || c.campaignId}>
                    <td style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#94a3b8" }}>
                      {c.campaignId}
                    </td>
                    <td style={{ fontWeight: 600 }}>{c.title}</td>
                    <td>
                      <span style={{ fontSize: "0.75rem", color: "#cbd5e1" }}>
                        {c.type === "manual" ? "📢 Manual Blast" : c.type === "rate_alert" ? "⚡ Rate Alert" : c.type}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: "0.75rem", textTransform: "capitalize" }}>{c.targetAudience}</span>
                    </td>
                    <td style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                      {new Date(c.createdAt).toLocaleString("en-US", { timeZone: "Asia/Kolkata" })}
                    </td>
                    <td style={{ color: "#86efac", fontWeight: 700 }}>
                      {c.stats?.acceptedByProvider ?? c.stats?.successful ?? 0}
                    </td>
                    <td style={{ color: "#fca5a5" }}>{c.stats?.failed ?? 0}</td>
                    <td>
                      <Badge $type={c.status}>
                        {c.status === "completed" || c.status === "accepted_by_fcm"
                          ? "FCM Accepted"
                          : c.status === "failed"
                          ? "Failed"
                          : c.status}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableWrap>
        <div style={{ marginTop: "12px", fontSize: "0.75rem", color: "#64748b", lineHeight: "1.4" }}>
          ℹ️ <strong>Delivery Reporting Note:</strong> &ldquo;FCM Accepted&rdquo; indicates that Google&rsquo;s Firebase Cloud Messaging servers received and queued the message for delivery. End-device lockscreen rendering depends on device battery optimization, network connectivity, and OS push permissions.
        </div>
      </Card>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              background: "#111827",
              border: "1.5px solid #f5d76e",
              borderRadius: "14px",
              maxWidth: "480px",
              width: "100%",
              padding: "24px",
              color: "#fff",
            }}
          >
            <h3 style={{ margin: "0 0 12px 0", color: "#f5d76e", fontSize: "1.2rem" }}>
              Confirm Notification Broadcast
            </h3>
            <p style={{ color: "#cbd5e1", fontSize: "0.85rem", lineHeight: 1.4 }}>
              Are you sure you want to broadcast this notification to{" "}
              <strong>{audience.toUpperCase()}</strong> subscribers?
            </p>

            <div
              style={{
                background: "#0b0f19",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #1f293d",
                fontSize: "0.82rem",
                margin: "14px 0",
              }}
            >
              <div><strong>Title:</strong> {title}</div>
              <div style={{ marginTop: "4px" }}><strong>Destination:</strong> {finalDestination}</div>
              <div style={{ marginTop: "4px" }}>
                <strong>Estimated Reach:</strong> ~{stats.promoConsented} opted-in subscribers
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                style={{
                  background: "#1f293d",
                  color: "#cbd5e1",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={handleSendCampaign}
                style={{
                  background: "#f5d76e",
                  color: "#000",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {sending ? "Broadcasting..." : "Yes, Broadcast Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default AdminNotificationsTab;
